/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {
  DetailsStageInterface,
  FormStateInterface, IssueFormData,
  PlatformData, ReportStageInterface,
} from '@/store/modules/form/types';
import {RootState} from '@/store/types';
import {api} from '@/services/http';
import {AxiosError, AxiosResponse} from 'axios';
import {state} from '@/store/modules/user-session';

export const actions: ActionTree<FormStateInterface, RootState> = {

    /**
     * Update stage.
     *
     * @param {any} commit
     * @param {number} newStage
     */
    updateStage({commit}: {commit: any}, newStage: number): void {
      commit('updateStage', newStage);
  },

  /******************************
   * Report stage 1. Report
   ******************************/

    /**
     * Update report stage 1.Report.
     *
     * @param {any} commit
     * @param {ReportStageInterface} reportNewStage
     */
    updateReportStage({commit}: {commit: any}, reportNewStage: ReportStageInterface): void {
      commit('updateReportStage', reportNewStage);
    },

  /**
   * Update selected game in report stage 1. Report
   *
   * @param commit
   * @param gameSelected
   */
  updateGameSelected({commit}: {commit: any}, gameSelected: number): void {
    commit('updateGameSelected', gameSelected);
  },


  /******************************
   * Report stage 3. Details
   ******************************/

  /**
   * Update report stage 3. Details.
   *
   * @param {any} commit
   * @param {DetailsStageInterface} newDetailsStage
   */
  updateDetailsStage({commit}: {commit: any}, newDetailsStage: DetailsStageInterface): void {
    commit('updateDetailsStage', newDetailsStage);
  },

  /******************************
   * Submit Report
   ******************************/

  /**
   * Submit report for issue.
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportIssue({commit}: {commit: any}, formData: IssueFormData): Promise<number | void>  {

    return api.axios.post('api/issues', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      // Return the issue id
      return +response.data.id;

    }, () => {
          // Handle error
        },
    );

  },


  /**
   * Submit report for issue details.
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportIssueDetails({commit}: {commit: any}, formData: any): Promise<any>  {

    return api.axios.post('api/issue_details', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      // Return the issue details id
      return +response.data.id;

    }, (err: AxiosError) => {
      return err;
    });

  },


  /**
   * Submit report for PC.
   *
   * todo model formData
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportPC({commit}: {commit: any}, formData: any): Promise<AxiosResponse | void>  {

    return api.axios.post('api/pc_platforms', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response;

    }, () => {
      // handle error
    });

  },

  /**
   * Submit report for PS4.
   *
   * todo model formData
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportPS4({commit}: {commit: any}, formData: any): Promise<AxiosResponse | void> {

    return api.axios.post('api/ps_platforms', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response;

    }, () => {
      // handle error
    });

  },

  /**
   * Submit report for XBOX.
   *
   * todo model formData
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportXBOX({commit}: {commit: any}, formData: any): Promise<AxiosResponse | void> {


    return api.axios.post('api/xbox_platforms', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response;

      }, () => {
        // handle error
    });
  },

    /**
     * Submit report for any platform.
     *
     * @param {any} commit
     * @param {any} formData
     */
    async submitReportPlatformGeneric({commit}: { commit: any }, formData: any):
        Promise<AxiosResponse<PlatformData> | void> {
        try {
            let url: string = '';
            switch (formData.type) {
                case 'ps5':
                    url = 'api/ps5_platforms';
                    break;
                case 'xbx':
                    url = 'api/xbox_x_platforms';
                    break;
                case 'xbs':
                    url = 'api/xbox_s_platforms';
                    break;
                case 'mac':
                    url = 'api/mac_platforms';
                    break;
                case 'nsw':
                    url = 'api/nintendo_switch_platforms';
                    break;
                default:
                    throw new Error('No url found');
            }

            const result: AxiosResponse = await api.axios.post(url, formData, {
                headers: {
                    Authorization: state.session.token,
                },
                params: {
                    skipAuthorization: true,
                },
            });

            return result;
      } catch (e) {
          // handle error
      }
    },

  /**
   * Submit report for NSW - Nintendo Switch.
   *
   * @param {any} commit
   * @param {any} formData
   */
  async submitReportSwitch({commit}: {commit: any}, formData: any): Promise<AxiosResponse | void> {
    return api.axios.post('api/nsw_platforms', formData, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response;

    }, () => {
      // handle error
    });
  },

  /**
   * Upload File - Get the preSign url.
   *
   * @param {any} commit
   * @param {any} file
   */
  async uploadFile({commit}: {commit: any}, file: any): Promise<any> {

    return api.axios.post('/api/files', file, {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response.data;

    }, (err: AxiosError) => {
      return err;
    });
  },


  /**
   * Upload File using the preSigned Url.
   *
   * @param {any} commit
   * @param {any} file
   */
  async uploadFilePreSignedUrl({commit}: {commit: any}, file: any): Promise<any> {

    return api.axios.put(file.preSignedUrl, file.file,  {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response.status;

    }, (err: AxiosError) => {
      return err;
    });
  },

  /**
   * Connect file to issue.
   *
   * @param {any} commit
   * @param {{issueDetailsId: number, fileIdsData: string[]}} file
   */
  async connectFileToIssue(
      {commit}: {commit: any},
      file: {issueDetailsId: number, fileIdsData: string[]},
  ): Promise<any> {
    return api.axios.post('/api/issue_details/' + file.issueDetailsId + '/evidence', file.fileIdsData,  {
      headers: {
        Authorization: state.session.token,
      },
      params: {
        skipAuthorization: true,
      },
    }).then((response: AxiosResponse) => {

      return response;

    }, (err: AxiosError) => {
      return err;
    });
  },

  /**
   * Action used to cancel the report issue.
   * @param {any} commit
   */
  cancelReportIssue({commit}: {commit: any}): void {
    commit('cancel', {});
  },

  /**
   * Subscription for issue.
   *
   * @param {any}
   * @param {boolean} subscription
   */
  subscribeIssue({commit}: {commit: any}, subscription: boolean): void {
    commit('subscribeIssue', subscription);
  },

   async subscribe({commit}: {commit: any}, issueId: string): Promise<any> {
       return api.axios.post('/api/customer_notifications', {issue: '/api/issues/' + issueId},  {
           headers: {
               Authorization: state.session.token,
           },
           params: {
               skipAuthorization: true,
           },
       }).then((response: AxiosResponse) => {
           return response;
       }, (err: AxiosError) => {
           return err;
       });
   },
};
